import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { fetchVenues } from "../APIs/Venue/GetVenues";
import { useLoadingStatus } from "./useLoadingStatus";
import { FIVE_MINUTES, THIRTY_MINUTES } from "../config/queryConfig";
import { VenuePage } from "../types/venues";

export const useGetVenues = (venueGroupId: string | undefined) => {
  const { updateStage } = useLoadingStatus();
  const queryClient = useQueryClient();
  const venuesQueryKey = ["venues", venueGroupId];

  const { data, refetch, isLoading, isError, error } = useQuery({
    queryKey: venuesQueryKey,
    queryFn: () => fetchVenues(venueGroupId),
    retry: 3,
    retryDelay: 3000,
    // staleTime (5 min): the venue list changes rarely, so render it from cache
    //   immediately on remount; a manual refetchVenues() or key change refreshes
    //   it, and it becomes eligible for a background refresh after 5 minutes.
    // gcTime (30 min): raised above the staleTime/refresh window so the cached
    //   list survives brief unmounts instead of being collected and refetched.
    staleTime: FIVE_MINUTES,
    gcTime: THIRTY_MINUTES,
    enabled: !!venueGroupId,
    // Seed the first render from the persisted-then-restored cache so a
    // returning user sees the venue list immediately instead of a loading
    // state. initialDataUpdatedAt carries the restored timestamp so staleTime
    // still governs the background refresh, and an empty cache (first-ever
    // load) yields undefined, preserving the genuine loading/empty path.
    initialData: () => queryClient.getQueryData<VenuePage[]>(venuesQueryKey),
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(venuesQueryKey)?.dataUpdatedAt
  });

  useEffect(() => {
    if (!venueGroupId) {
      return;
    }

    if (isLoading) {
      updateStage("venues", "loading");
    } else if (isError) {
      updateStage("venues", "error", {
        message: "Failed to fetch venue list. Please try again.",
        technicalDetails: error instanceof Error ? error.message : String(error)
      });
    } else if (data !== undefined) {
      if (data.length === 0) {
        updateStage("venues", "error", {
          message: "No venues found for this organisation.",
          technicalDetails: `fetchVenues() returned empty array for venueGroupId: ${venueGroupId}`
        });
      } else {
        updateStage("venues", "success");
      }
    }
  }, [venueGroupId, isLoading, isError, data, error, updateStage]);

  return {
    allVenues: data,
    refetchVenues: refetch,
    areVenuesLoading: isLoading,
    hasVenuesError: isError,
    venuesError: error
  };
};
